import { useState } from 'react';

import { getErrorMessage } from '@/utils/errors';
import {
  Box,
  CodeBlock,
  Flex,
  Heading,
  Icon,
  Link,
  Modal,
  ModalBody,
  Paragraph,
} from '@customerio/pluma-components/react';
import type { FallbackProps } from 'react-error-boundary';

/**
 * Default fallback UI for `AppErrorBoundary`.
 * Shows a left-aligned inline error with retry and details modal.
 */
export const AppErrorFallback = ({ error, resetErrorBoundary }: FallbackProps) => {
  const [showDetails, setShowDetails] = useState(false);

  return (
    <>
      <Box p="300">
        <Flex gap="075" alignItems="center" mb="050">
          <Icon name="error" size="md" />
          <Heading level="3">Something went wrong</Heading>
        </Flex>
        <Flex direction="column" gap="025">
          <Paragraph color="subtle">
            Our team has been notified. Please try again or contact support if the issue persists.
          </Paragraph>
        </Flex>
        <Flex mt="075" gap="100">
          <Link onClick={resetErrorBoundary}>Try again</Link>
          <Link
            icon="chevron-right"
            iconPosition="trailing"
            onClick={() => {
              setShowDetails(true);
            }}
          >
            Show details
          </Link>
        </Flex>
      </Box>
      <Modal
        isOpen={showDetails}
        onClose={() => {
          setShowDetails(false);
        }}
        title="Error details"
        size="md"
      >
        <ModalBody>
          <CodeBlock
            code={getErrorMessage(error)}
            language="plain"
            mode="light"
            withCopyButton={true}
            withLineNumbers={false}
          />
        </ModalBody>
      </Modal>
    </>
  );
};
